import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, Download, ArrowRight, Shield, ExternalLink, Loader2 } from 'lucide-react';
import { verifyAssessmentPurchase, getAssessment } from '../lib/api';
import { toast } from 'sonner';
import LegalFooter from '../components/LegalFooter';

export default function AssessmentPurchaseSuccess() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [assessment, setAssessment] = useState(null);

  const sessionId = searchParams.get('session_id');
  const assessmentId = searchParams.get('assessment_id');

  useEffect(() => {
    if (sessionId && assessmentId) {
      verifyPurchase();
    } else if (assessmentId) {
      // No session ID, just load the assessment
      loadAssessment();
    } else {
      setLoading(false);
    }
  }, [sessionId, assessmentId]);

  const verifyPurchase = async () => {
    try {
      const response = await verifyAssessmentPurchase(assessmentId, sessionId);
      if (response.data.success) {
        setVerified(true);
        setDownloadUrl(response.data.download_url);
        setAssessment(response.data.assessment);
      } else {
        toast.error('Payment verification failed');
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Failed to verify purchase');
    } finally {
      setLoading(false);
    }
  };

  const loadAssessment = async () => {
    try {
      const response = await getAssessment(assessmentId);
      setAssessment(response.data);
      if (response.data.purchased_pack) {
        setVerified(true);
        // Use the known PDF URL
        setDownloadUrl("https://customer-assets.emergentagent.com/job_tier-registry/artifacts/i1g4ww4u_AI-Governance-Starter-Pack.pdf");
      }
    } catch (error) {
      console.error('Load assessment error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <header className="border-b border-zinc-200">
          <div className="container mx-auto px-6 py-4">
            <Link to="/" className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              AI Accountability Standards Council
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-600">Verifying your purchase...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!verified) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <header className="border-b border-zinc-200">
          <div className="container mx-auto px-6 py-4">
            <Link to="/" className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              AI Accountability Standards Council
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <p className="text-zinc-600 mb-6">
              We couldn't verify your purchase. If you completed payment, please contact support.
            </p>
            <Link to="/assessment">
              <Button variant="outline" className="rounded-none">
                Return to Assessment
              </Button>
            </Link>
          </div>
        </main>
        <LegalFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="text-xl font-semibold text-zinc-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            AI Accountability Standards Council
          </Link>
        </div>
      </header>

      <main className="flex-1">
        {/* Success Message */}
        <section className="py-16 px-6 bg-emerald-50 border-b border-emerald-200">
          <div className="container mx-auto max-w-3xl text-center">
            <div className="w-16 h-16 bg-emerald-100 border border-emerald-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 
              className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Purchase Complete
            </h1>
            <p className="text-lg text-zinc-600 mb-2">
              Thank you{assessment?.name ? `, ${assessment.name}` : ''}!
            </p>
            <p className="text-zinc-600">
              Your AI Governance Documentation System is ready for download.
            </p>
          </div>
        </section>

        {/* Download Section */}
        <section className="py-12 px-6">
          <div className="container mx-auto max-w-3xl">
            <div className="bg-white border border-zinc-200 p-8 text-center mb-8">
              <Download className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Download Your Documentation System
              </h2>
              <p className="text-zinc-600 mb-6">
                Click below to download your complete AI Governance Documentation System.
              </p>
              <a 
                href={downloadUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button 
                  size="lg" 
                  className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                  data-testid="download-btn"
                >
                  <Download className="mr-2 w-5 h-5" />
                  Download PDF
                </Button>
              </a>
              <p className="text-sm text-zinc-500 mt-4">
                A copy has also been sent to your email.
              </p>
            </div>

            {/* What's Inside */}
            <div className="bg-zinc-50 border border-zinc-200 p-6 mb-8">
              <h3 className="font-semibold text-zinc-900 mb-4">What's Inside Your Pack</h3>
              <ul className="space-y-3 text-zinc-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>AI Acceptable Use Policy - Ready to customize</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>AI Tool Inventory Template - Track all AI tools</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Vendor Risk Scorecards - Evaluate vendors</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Incident Tracking System - Log issues</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Employee AI Agreements - Get acknowledgment</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Risk Summary Report - Document your posture</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Alignment Declared Section */}
        <section className="py-12 px-6 bg-zinc-900 text-white">
          <div className="container mx-auto max-w-3xl text-center">
            <Shield className="w-12 h-12 text-zinc-400 mx-auto mb-6" />
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              You now have the documentation required for Alignment Declared status
            </h2>
            <p className="text-zinc-400 mb-8 max-w-xl mx-auto">
              This documentation establishes the foundation for demonstrating AI governance. 
              It positions your business for the next level of validation.
            </p>
          </div>
        </section>

        {/* Next Steps Section */}
        <section className="py-12 px-6">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-2xl font-semibold text-zinc-900 mb-6 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Next Step: Verification
            </h2>
            
            <div className="bg-white border border-zinc-200 p-8">
              <p className="text-zinc-700 mb-6">
                Ready to prove your controls are implemented? Submit for <strong>Verification</strong> to 
                confirm your AI governance system is operational and evidenced.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="font-medium text-zinc-900 mb-2">What is Verification?</h4>
                  <p className="text-sm text-zinc-600">
                    Independent review of your AI governance implementation. Confirms controls are 
                    not just documented, but actually in place.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-zinc-900 mb-2">Why it matters</h4>
                  <p className="text-sm text-zinc-600">
                    Verification demonstrates to clients, partners, and stakeholders that your 
                    AI governance is real and operational.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://aiaccountabilitycouncil.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button className="rounded-none bg-zinc-900 hover:bg-zinc-800 w-full sm:w-auto">
                    Learn About Verification
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </Button>
                </a>
                <Link to="/apply">
                  <Button variant="outline" className="rounded-none w-full sm:w-auto">
                    Apply Now
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-12 px-6 bg-zinc-50 border-t border-zinc-200">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-xl font-semibold text-zinc-900 mb-6 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Your Progress
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-zinc-100 border border-zinc-300 p-6">
                <h4 className="font-medium text-zinc-500 mb-4 text-sm uppercase tracking-wide">Before</h4>
                <ul className="space-y-2 text-zinc-600">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                    No documentation
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                    No accountability
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                    No audit trail
                  </li>
                </ul>
              </div>
              
              <div className="bg-emerald-50 border border-emerald-200 p-6">
                <h4 className="font-medium text-emerald-700 mb-4 text-sm uppercase tracking-wide">Now</h4>
                <ul className="space-y-2 text-zinc-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    Structured governance
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    Defined controls
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    Evidence-backed processes
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>

      <LegalFooter />
    </div>
  );
}
